import { Outlet, useLoaderData, useMatches } from '@remix-run/react';
import Layout from '~/components/layout/Layout';
import { json } from '@remix-run/node';
import type { LoaderFunction } from '@remix-run/node';
import { fetchStrapi } from '~/lib/server/api.server';
import type { CardsPage } from '@cardo/types';
import { generateMetaFromSeoComponent } from '~/lib/meta';
import { isPrefetch } from 'remix-utils/is-prefetch';
import PageNavLink from '~/components/PageNavLink';
import { getCategoryIcon } from '@cardo/lib';
import HTMLContent from '~/components/blocks/HTMLContent';
import { DefaultErrorBoundary } from '@cardo/ui';
import type { MetaFunction } from '@remix-run/react';

export const ErrorBoundary = DefaultErrorBoundary;

export const meta: MetaFunction = ({ data }: { data: CardsPage }) => {
  return [
    ...generateMetaFromSeoComponent(data?.attributes.seo),
    { title: 'Cardonomics - Credit Cards' },
  ];
};

export function headers({ loaderHeaders }: { loaderHeaders: Headers }) {
  return { 'cache-control': loaderHeaders.get('cache-control') };
}

export const loader: LoaderFunction = async ({ request, params }) => {
  const { categorySlug } = params;

  const res = await fetchStrapi<CardsPage>('/cards-page', {
    populate: ['seo', 'filterByCategories'],
  });

  let headers = new Headers({
    'cache-control': 'max-age=1, s-maxage=600, stale-while-revalidate=31536000',
  });

  if (isPrefetch(request)) {
    headers.set('Cache-Control', 'private, max-age=10, s-maxage=0');
  }

  if (!categorySlug) {
    return json(res.data, {
      status: 302,
      headers: {
        ...headers,
        Location: `/cards/${res.data?.attributes.filterByCategories.data[0]?.attributes?.slug}`,
      },
    });
  }

  return json(res.data, { headers });
};

export default function Cards() {
  const data = useLoaderData() as unknown as CardsPage;

  const categoriesWithSlugs = data.attributes.filterByCategories.data.filter(
    (category) => category?.attributes?.slug
  );

  const matches = useMatches();
  const cardCategoryMatch = matches.find(
    (match) => match.id === 'routes/cards.$categorySlug'
  );
  const categoryInFilterByList = categoriesWithSlugs.find(
    (cardsPageCategory) =>
      cardsPageCategory?.attributes?.slug ===
      cardCategoryMatch?.data?.attributes?.slug
  );

  let lastUpdated;

  try {
    lastUpdated = new Date(
      cardCategoryMatch?.data?.attributes.updatedAt
    ).toLocaleDateString('en-us');
  } catch (err) {
    console.error('Error parsing updatedAt: ', err);
  }

  return (
    <Layout>
      <div className="relative mx-auto w-full pt-20 xl:w-4/5">
        <div className="-mt-60 w-full">
          <div className="bg-theme-blue-darkest mx-auto flex h-full w-full flex-col justify-between rounded-lg p-8 drop-shadow-xl">
            <h1 className="mb-2 text-2xl text-white sm:text-4xl">
              {data.attributes.heading || (
                <span>Our Top Cards of {new Date().getFullYear()}</span>
              )}
            </h1>
            <div className="flex flex-col space-y-4 sm:space-y-8">
              {cardCategoryMatch?.data?.attributes.editorial && (
                <div className="mt-4 flex flex-col gap-2 sm:gap-4">
                  <HTMLContent
                    content={cardCategoryMatch?.data?.attributes.editorial}
                    className="prose-headings:text-white text-white"
                  />
                  <p className="text-xs text-white sm:text-sm">
                    {cardCategoryMatch?.data?.attributes.authorName && (
                      <span>
                        By {cardCategoryMatch?.data?.attributes?.authorName}
                      </span>
                    )}
                    {cardCategoryMatch?.data?.attributes.authorName &&
                      lastUpdated && <span className="mx-2">|</span>}
                    {lastUpdated && <span>Updated on {lastUpdated}</span>}
                  </p>
                </div>
              )}
              {!cardCategoryMatch?.data?.attributes.editorial && (
                <p className="text-white sm:text-lg">
                  {data.attributes.subheading || 'Filter by'}
                </p>
              )}
              <div className="flex flex-col flex-wrap gap-4 sm:flex-row sm:gap-6">
                {categoriesWithSlugs.map((category) => (
                  <PageNavLink
                    to={category.attributes.slug}
                    key={category.attributes.slug}
                    icon={
                      category.attributes.icon
                        ? getCategoryIcon(category.attributes.icon)
                        : undefined
                    }
                  >
                    {category.attributes?.name}
                  </PageNavLink>
                ))}
                {!categoryInFilterByList && (
                  <PageNavLink
                    to={cardCategoryMatch?.data?.attributes.slug}
                    icon={
                      cardCategoryMatch?.data?.attributes.icon
                        ? getCategoryIcon(
                            cardCategoryMatch.data.attributes.icon
                          )
                        : undefined
                    }
                  >
                    {cardCategoryMatch?.data?.attributes?.name}
                  </PageNavLink>
                )}
                {categoriesWithSlugs.length === 0 && (
                  <div className="rounded-lg bg-slate-50 p-5">
                    <p className="text-rose-700">
                      Uh oh, looks like something's broken. In the meantime,
                      check out our partner's page{' '}
                      <a href="https://www.cardratings.com">CardRatings.com</a>.
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
        <div className="py-4 sm:py-8">
          <Outlet />
        </div>
      </div>
    </Layout>
  );
}
